import React, { useCallback, useEffect, useState } from 'react'
import { FileChip } from 'components/Shared/FileChip'
import { useErrorHandling } from 'context/errorHandling'
import ApiError from 'constants/ApiError'
import { HTTP_500_ERROR } from 'Constants'
import { Dropzone } from 'components/Shared/Dropzone'
import { useTranslation } from 'react-i18next'

export function AttachmentsDropzone ({
  initialAttachments = [],
  onAttachmentsUpdated = () => {},
  uploadAttachment,
  label,
  disabled,
  dropzoneStyles,
  chipContainerStyles,
  onAttachmentRemoved = () => {},
  onAttachmentVerifiedChange = () => {},
  onAttachmentDriverAccessChange = () => {},
  onAttachmentContractorAccessChange = () => {},
  chipInputProps = {},
  shouldUpdateOnChange
}) {
  const { handleError } = useErrorHandling()
  const { t } = useTranslation()

  const mapInitialAttachments = useCallback(() => initialAttachments.map(file => ({
    filename: file.filename,
    id: file.id,
    verified: file.verified,
    attachmentAccess: {
      contractorAccess: file.attachmentAccess.contractorAccess,
      driverAccess: file.attachmentAccess.driverAccess
    }
  })), [initialAttachments])

  const [files, setFiles] = useState(mapInitialAttachments)

  useEffect(() => {
    if (shouldUpdateOnChange) {
      setFiles(mapInitialAttachments())
    }
  }, [shouldUpdateOnChange, mapInitialAttachments])

  const onDrop = useCallback(newFiles => {
    Promise.all(
      newFiles.map(uploadAttachment))
      .then(response => {
        const addedFiles = response
          .map(file => ({
              filename: file.filename,
              id: file.id,
              verified: file.verified,
              attachmentAccess: file.attachmentAccess
            })
          )

        const updatedFiles = [...files, ...addedFiles]
        setFiles(updatedFiles)
        onAttachmentsUpdated(updatedFiles)
      }).catch(error => handleError(error, {
        [HTTP_500_ERROR]: ApiError.FileUploadFailure
      })
    )

  }, [files, handleError, onAttachmentsUpdated, uploadAttachment])

  function handleRemove (fileToRemove) {
    const updatedFiles = files.filter(file => fileToRemove.id !== file.id)
    setFiles(updatedFiles)
    onAttachmentsUpdated(updatedFiles)
    onAttachmentRemoved(fileToRemove)
  }

  function handleVerifiedChange (value, id) {
    const updatedFiles = files.map(file => {
      if (file.id === id) {
        file.verified = value
      }
      return file
    })
    setFiles(updatedFiles)
    onAttachmentsUpdated(updatedFiles)
    onAttachmentVerifiedChange(id, value)
  }

  function handleDriverAccessChange (value, id) {
    const updatedFiles = files.map(file => {
      if (file.id === id) {
        file.attachmentAccess.driverAccess = value
      }
      return file
    })
    setFiles(updatedFiles)
    onAttachmentsUpdated(updatedFiles)
    onAttachmentDriverAccessChange(id, value)
  }

  function handleContractorAccessChange (value, id) {
    const updatedFiles = files.map(file => {
      if (file.id === id) {
        file.attachmentAccess.contractorAccess = value
      }
      return file
    })
    setFiles(updatedFiles)
    onAttachmentsUpdated(updatedFiles)
    onAttachmentContractorAccessChange(id, value)
  }

  return (
    <div className={dropzoneStyles}>
      {<h2>{t(label)}</h2>}
      <Dropzone files={files}
                onDrop={onDrop}
                disabled={disabled}
                chipContainerStyles={chipContainerStyles}
                chipRenderer={file =>
                  <FileChip key={file.id}
                            file={file}
                            onRemove={handleRemove}
                            disabled={disabled}
                            accessDetails={!disabled && {
                              verified: file.verified,
                              contractorAccess: file.attachmentAccess.contractorAccess,
                              driverAccess: file.attachmentAccess.driverAccess,
                              verifiedClicked: (value) => {
                                handleVerifiedChange(value, file.id)
                              },
                              contractorAccessClicked: (value) => {
                                handleContractorAccessChange(value, file.id)
                              },
                              driverAccessClicked: (value) => {
                                handleDriverAccessChange(value, file.id)
                              },
                              cursorPointer: true
                            }}
                            {...chipInputProps}
                  />}
      />
    </div>
  )
}
